import {
  canonicalClientId,
  ESI_CLI_CLIENT_ID,
  type NativeClientInfo,
  type NativeClientsPolicy,
  nativeClientIdSchema,
  nativeClientInfoSchema,
} from "@esi/shared";
import { useQueries } from "@tanstack/react-query";
import { AlertCircle, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ok } from "../api-errors.ts";
import { useAdminApi } from "./admin-api.tsx";

/**
 * The editor of `auth.nativeClients` (openspec change native-device-and-cimd, D3): any
 * application, or only the listed client IDs. A client is added by its ID; its name is fetched
 * from its metadata document (through the authorization server's guarded fetcher) before it
 * joins the list, so the admin sees what they allow. ESI's own command-line tool is always
 * allowed and never listed.
 */
export function NativeClientsEditor(props: {
  id: string;
  value: NativeClientsPolicy;
  onChange: (value: NativeClientsPolicy) => void;
}) {
  const { id, value, onChange } = props;
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [known, setKnown] = useState<Record<string, NativeClientInfo>>({});
  const lookup = async (clientId: string) =>
    nativeClientInfoSchema.parse(
      await ok(await admin["oauth-clients"].$get({ query: { id: clientId } })),
    );
  // Names of the clients already listed (a failed fetch shows the ID's domain only).
  const listed = useQueries({
    queries: value.clients.map((clientId) => ({
      queryKey: ["admin", "oauth-client", clientId],
      queryFn: () => lookup(clientId),
      enabled: !known[clientId],
      retry: false,
      staleTime: 5 * 60_000,
    })),
  });

  // Not a form of its own: the editor sits inside the setting row's form, and submitting
  // there would save the policy before the client's name was fetched.
  async function add() {
    const clientId = draft.trim();
    const parsed = nativeClientIdSchema.safeParse(clientId);
    if (!parsed.success || clientId === ESI_CLI_CLIENT_ID) {
      setError(
        clientId === ESI_CLI_CLIENT_ID
          ? t("adminSettings.nativeClients.alwaysAllowed")
          : t("adminSettings.nativeClients.invalidId"),
      );
      return;
    }
    // The same client however it is spelled (host case, default port), as the server compares.
    if (value.clients.some((listed) => canonicalClientId(listed) === canonicalClientId(clientId))) {
      setDraft("");
      return;
    }
    setAdding(true);
    setError(null);
    try {
      const info = await lookup(clientId);
      setKnown((k) => ({ ...k, [clientId]: info }));
      onChange({ ...value, clients: [...value.clients, clientId] });
      setDraft("");
    } catch {
      setError(t("adminSettings.nativeClients.unreadable"));
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <RadioGroup
        aria-labelledby={`${id}-label`}
        // No other edit while a client is being looked up: the addition applies to this value.
        disabled={adding}
        value={value.mode}
        onValueChange={(mode) => onChange({ ...value, mode: mode as NativeClientsPolicy["mode"] })}
      >
        {(["any", "allowlist"] as const).map((mode) => (
          <div key={mode} className="flex items-center gap-2.5">
            <RadioGroupItem id={`${id}-${mode}`} value={mode} />
            <Label htmlFor={`${id}-${mode}`} className="font-normal">
              {t(`adminSettings.nativeClients.${mode}`)}
            </Label>
          </div>
        ))}
      </RadioGroup>
      {value.mode === "allowlist" ? (
        <div className="flex flex-col gap-3">
          {value.clients.length > 0 ? (
            <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
              {value.clients.map((clientId, index) => {
                const info = known[clientId] ?? listed[index]?.data;
                const host = URL.canParse(clientId) ? new URL(clientId).hostname : clientId;
                return (
                  <li key={clientId} className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-fg">{info?.name ?? host}</p>
                      <p className="break-all text-xs text-fg-muted">{clientId}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={adding}
                      aria-label={t("adminSettings.nativeClients.remove", {
                        client: info?.name ?? host,
                      })}
                      onClick={() =>
                        onChange({ ...value, clients: value.clients.filter((c) => c !== clientId) })
                      }
                    >
                      <X aria-hidden />
                    </Button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-fg-secondary">{t("adminSettings.nativeClients.empty")}</p>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-add`} className="text-xs text-fg-secondary">
              {t("adminSettings.nativeClients.addLabel")}
            </Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id={`${id}-add`}
                value={draft}
                placeholder="https://"
                inputMode="url"
                spellCheck={false}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${id}-add-error` : undefined}
                onChange={(e) => setDraft(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void add();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={adding || draft.trim() === ""}
                onClick={() => void add()}
              >
                {adding
                  ? t("adminSettings.nativeClients.checking")
                  : t("adminSettings.nativeClients.add")}
              </Button>
            </div>
            {error ? (
              <p
                id={`${id}-add-error`}
                className="flex items-center gap-1.5 text-sm text-danger-fg"
              >
                <AlertCircle className="size-3.5 shrink-0" aria-hidden />
                {error}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      <p className="text-xs text-fg-muted">{t("adminSettings.nativeClients.firstParty")}</p>
    </div>
  );
}
